import React from 'react';
import { designSystem } from '@yola/ws-ui';
import getValidationRules from './get-validation-rules';
import {
  serviceValues,
  newWebsitePreparedContentValues,
  newWebsiteContentTypeValues,
  migrationPlatformValues,
  migrationReasonValues,
  OTHER_VALUE,
} from '../constants/fields-values';
import fieldNames from '../constants/field-names';
import fieldIds from '../constants/field-ids';
import fieldTypes from '../constants/field-types';
import { t } from '../../../localization';

const { Mark } = designSystem;

const getFields = () => {
  const rules = getValidationRules();

  return [
    {
      id: fieldIds.project,
      name: fieldNames.SERVICE,
      summaryTitle: t('Project'),
      rules: rules[fieldNames.SERVICE],
      type: fieldTypes.CARD,
      items: [
        {
          id: serviceValues.BUILD_NEW_WEBSITE,
          title: t('Build a brand-new website'),
          description: t('Get your dream website built from scratch with Yola Sitebuilder+'),
          icon: 'brush',
        },
        {
          id: serviceValues.REVAMP_EXISTING_WEBSITE,
          title: t('Revamp your website'),
          description: t('Get the website you’ve already built enhanced and refined'),
          icon: 'magic',
        },
        {
          id: serviceValues.MIGRATE_WEBSITE,
          title: t('Migrate external website to Yola'),
          description: t('Get your existing website rebuilt on Yola Sitebuilder+'),
          icon: 'repeat',
        },
      ],
    },
    {
      id: fieldIds.newWebsiteDescription,
      name: fieldNames.NEW_WEBSITE_DESCRIPTION,
      required: !!rules[fieldNames.NEW_WEBSITE_DESCRIPTION].required,
      title: t('What is the nature of your business and purpose of your website?'),
      statusText: t('Please add as many details as possible'),
      placeholder: t(
        `Example: A local florist offline store. I want to generate leads via the website, showcase my work, add an online store. I want my site’s color palette to be blue, i’d like to make it more visually appealing.`
      ),
      rules: rules[fieldNames.NEW_WEBSITE_DESCRIPTION],
      maxLength: rules[fieldNames.NEW_WEBSITE_DESCRIPTION].maxLength.value,
      type: fieldTypes.TEXTAREA,
    },
    {
      id: fieldIds.newWebsitePreparedContent,
      name: fieldNames.NEW_WEBSITE_CONTENT,
      required: !!rules[fieldNames.NEW_WEBSITE_CONTENT].required,
      title: t('Do you already have content for your website?'),
      rules: rules[fieldNames.NEW_WEBSITE_CONTENT],
      type: fieldTypes.RADIO,
      options: [
        {
          label: <b>{t('Yes')}</b>,
          summaryLabel: t('Yes'),
          value: newWebsitePreparedContentValues.YES,
        },
        {
          label: <b>{t('No')}</b>,
          summaryLabel: t('No'),
          value: newWebsitePreparedContentValues.NO,
        },
      ],
      subFields: {
        [newWebsitePreparedContentValues.YES]: fieldIds.newWebsitePreparedContentTypes,
      },
    },
    {
      id: fieldIds.newWebsitePreparedContentTypes,
      parentId: fieldIds.newWebsitePreparedContent,
      name: fieldNames.NEW_WEBSITE_CONTENT_TYPES,
      title: t('What content elements do you currently have ready for your website?'),
      description: t('Please select all that apply'),
      isSubField: true,
      type: fieldTypes.CHECKBOX,
      options: [
        {
          label: t('Logo'),
          value: newWebsiteContentTypeValues.LOGO,
        },
        {
          label: (
            <React.Fragment>
              <b>{t('Media')}</b>{' '}
              <Mark appearance="medium-emphasis">({t('images/photos/video')})</Mark>
            </React.Fragment>
          ),
          summaryLabel: t('Media'),
          isLabelBold: false,
          value: newWebsiteContentTypeValues.MEDIA,
        },
        {
          label: (
            <React.Fragment>
              <b>{t('Text materials')}</b>{' '}
              <Mark appearance="medium-emphasis">({t('written content, articles')})</Mark>
            </React.Fragment>
          ),
          summaryLabel: t('Text materials'),
          isLabelBold: false,
          value: newWebsiteContentTypeValues.TEXT_MATERIALS,
        },
        {
          label: t('Old website'),
          value: newWebsiteContentTypeValues.OLD_WEBSITE,
        },
        {
          label: (
            <React.Fragment>
              <b>{t('Other')}</b> <Mark appearance="medium-emphasis">({t('please specify')})</Mark>
            </React.Fragment>
          ),
          isLabelBold: false,
          value: OTHER_VALUE,
        },
      ],
      subFields: {
        [OTHER_VALUE]: fieldIds.newWebsitePreparedContentOther,
      },
    },
    {
      id: fieldIds.newWebsitePreparedContentOther,
      parentId: fieldIds.newWebsitePreparedContentTypes,
      name: fieldNames.NEW_WEBSITE_CONTENT_OTHER_TYPE,
      statusText: t('Please add as many details as possible'),
      isSubField: true,
      rules: rules[fieldNames.NEW_WEBSITE_CONTENT_OTHER_TYPE],
      maxLength: rules[fieldNames.NEW_WEBSITE_CONTENT_OTHER_TYPE].maxLength.value,
      type: fieldTypes.TEXTAREA,
    },
    {
      id: fieldIds.newWebsiteExpectedFeatures,
      name: fieldNames.NEW_WEBSITE_EXPECTED_FEATURES,
      required: !!rules[fieldNames.NEW_WEBSITE_EXPECTED_FEATURES].required,
      title: t('What features and functionality would you like to have on your website?'),
      statusText: t('Please add as many details as possible'),
      placeholder: t(
        `Example: I want to add an online store, add call-to-action buttons, add links to my email and phone number, add a cookie consent banner…`
      ),
      rules: rules[fieldNames.NEW_WEBSITE_EXPECTED_FEATURES],
      maxLength: rules[fieldNames.NEW_WEBSITE_EXPECTED_FEATURES].maxLength.value,
      type: fieldTypes.TEXTAREA,
    },
    {
      id: fieldIds.revampWebsiteDescription,
      name: fieldNames.WEBSITE_DESCRIPTION,
      required: !!rules[fieldNames.WEBSITE_DESCRIPTION].required,
      title: t('What is your site’s purpose and what kind of content does it present?'),
      statusText: t('Please add as many details as possible'),
      placeholder: t(
        `Example: A local florist offline store. It showcases my work, has customer testimonials on it, my store’s address and phone number…`
      ),
      rules: rules[fieldNames.WEBSITE_DESCRIPTION],
      maxLength: rules[fieldNames.WEBSITE_DESCRIPTION].maxLength.value,
      type: fieldTypes.TEXTAREA,
    },
    {
      id: fieldIds.revampWebsiteExpectedImprovements,
      name: fieldNames.WEBSITE_EXPECTED_IMPROVEMENTS,
      required: !!rules[fieldNames.WEBSITE_EXPECTED_IMPROVEMENTS].required,
      title: t('What would you like to improve your site with?'),
      statusText: t('Please add as many details as possible'),
      placeholder: t(
        'Example: I want to add an online store, add call-to-action buttons, add links to my email and phone number, add a cookie consent banner…'
      ),
      rules: rules[fieldNames.WEBSITE_EXPECTED_IMPROVEMENTS],
      maxLength: rules[fieldNames.WEBSITE_EXPECTED_IMPROVEMENTS].maxLength.value,
      type: fieldTypes.TEXTAREA,
    },
    {
      id: fieldIds.migrationWebsiteUrl,
      name: fieldNames.MIGRATION_WEBSITE_URL,
      required: !!rules[fieldNames.MIGRATION_WEBSITE_URL].required,
      title: t('Current website URL'),
      rules: rules[fieldNames.MIGRATION_WEBSITE_URL],
      type: fieldTypes.INPUT,
    },
    {
      id: fieldIds.migrationPlatform,
      name: fieldNames.MIGRATION_PLATFORM,
      required: !!rules[fieldNames.MIGRATION_PLATFORM].required,
      title: t('What platform or CMS do you want to migrate your website from?'),
      rules: rules[fieldNames.MIGRATION_PLATFORM],
      type: fieldTypes.RADIO,
      options: [
        {
          label: <b>WordPress</b>,
          summaryLabel: 'WordPress',
          value: migrationPlatformValues.WORD_PRESS,
        },
        {
          label: <b>Wix</b>,
          summaryLabel: 'Wix',
          value: migrationPlatformValues.WIX,
        },
        {
          label: <b>Squarespace</b>,
          summaryLabel: 'Squarespace',
          value: migrationPlatformValues.SQUARESPACE,
        },
        {
          label: <b>Joomla</b>,
          summaryLabel: 'Joomla',
          value: migrationPlatformValues.JOOMLA,
        },
        {
          label: <b>Shopify</b>,
          summaryLabel: 'Shopify',
          value: migrationPlatformValues.SHOPIFY,
        },
        {
          label: <b>Yola Sitebuilder Classic</b>,
          summaryLabel: 'Yola Sitebuilder Classic',
          value: migrationPlatformValues.YOLA_SITEBUILDER_CLASSIC,
        },
        {
          label: <b>{t('I don’t know')}</b>,
          summaryLabel: t('I don’t know'),
          value: migrationPlatformValues.I_DONT_KNOW,
        },
        {
          label: (
            <React.Fragment>
              <b>{t('Other')}</b> <Mark appearance="medium-emphasis">({t('please specify')})</Mark>
            </React.Fragment>
          ),
          isLabelBold: false,
          value: OTHER_VALUE,
        },
      ],
      subFields: {
        [OTHER_VALUE]: fieldIds.migrationPlatformOther,
      },
    },
    {
      id: fieldIds.migrationPlatformOther,
      parentId: fieldIds.migrationPlatform,
      name: fieldNames.MIGRATION_PLATFORM_OTHER,
      isSubField: true,
      rules: rules[fieldNames.MIGRATION_PLATFORM_OTHER],
      maxLength: rules[fieldNames.MIGRATION_PLATFORM_OTHER].maxLength.value,
      type: fieldTypes.TEXTAREA,
    },
    {
      id: fieldIds.migrationReason,
      name: fieldNames.MIGRATION_REASON,
      required: !!rules[fieldNames.MIGRATION_REASON].required,
      title: t('Why do you want to migrate from your existing platform?'),
      description: t('Please select all that apply'),
      rules: rules[fieldNames.MIGRATION_REASON],
      type: fieldTypes.CHECKBOX,
      options: [
        {
          label: t('It’s missing some features'),
          value: migrationReasonValues.MISSING_SOME_FEATURES,
        },
        {
          label: t('It’s too expensive'),
          value: migrationReasonValues.TOO_EXPENSIVE,
        },
        {
          label: (
            <React.Fragment>
              <b>{t('It’s hard to use')}</b>{' '}
              <Mark appearance="medium-emphasis">
                ({t('add new content, edit existing content')})
              </Mark>
            </React.Fragment>
          ),
          summaryLabel: t('It’s hard to use'),
          isLabelBold: false,
          value: migrationReasonValues.HARD_TO_USE,
        },
        {
          label: t('It’s not stable'),
          value: migrationReasonValues.NOT_STABLE,
        },
        {
          label: t('It’s poorly supported or outdated'),
          value: migrationReasonValues.POORLY_SUPPORTED,
        },
        {
          label: (
            <React.Fragment>
              <b>{t('Other')}</b> <Mark appearance="medium-emphasis">({t('please specify')})</Mark>
            </React.Fragment>
          ),
          isLabelBold: false,
          value: OTHER_VALUE,
        },
      ],
      subFields: {
        [OTHER_VALUE]: fieldIds.migrationReasonOther,
      },
    },
    {
      id: fieldIds.migrationReasonOther,
      parentId: fieldIds.migrationReason,
      name: fieldNames.MIGRATION_REASON_OTHER,
      statusText: t('Please add as many details as possible'),
      isSubField: true,
      rules: rules[fieldNames.MIGRATION_REASON_OTHER],
      maxLength: rules[fieldNames.MIGRATION_REASON_OTHER].maxLength.value,
      type: fieldTypes.TEXTAREA,
    },
    {
      id: fieldIds.migrationExpectedImprovements,
      name: fieldNames.MIGRATION_EXPECTED_IMPROVEMENTS,
      required: !!rules[fieldNames.MIGRATION_EXPECTED_IMPROVEMENTS].required,
      title: t('Is there anything you’d like to change or improve while migrating the website?'),
      statusText: t('Please add as many details as possible'),
      placeholder: t(
        'Example: I want to change my site’s color palette, make it more visually appealing. I want to add more visual content. I want to improve my website layout, add extra functionality, such as…  I want to add an online store, add call-to-action buttons, add links to my email and phone number, add a cookie consent banner…'
      ),
      rules: rules[fieldNames.MIGRATION_EXPECTED_IMPROVEMENTS],
      maxLength: rules[fieldNames.MIGRATION_EXPECTED_IMPROVEMENTS].maxLength.value,
      type: fieldTypes.TEXTAREA,
    },
    {
      id: fieldIds.contactInfoUserFullName,
      name: fieldNames.CONTACT_INFO_USER_FULL_NAME,
      required: !!rules[fieldNames.CONTACT_INFO_USER_FULL_NAME].required,
      title: t('Full name'),
      placeholder: t('e.g. John Doe'),
      rules: rules[fieldNames.CONTACT_INFO_USER_FULL_NAME],
      type: fieldTypes.INPUT,
      iconGlyph: 'user',
      size: 'medium',
    },
    {
      id: fieldIds.contactInfoUserEmail,
      name: fieldNames.CONTACT_INFO_USER_EMAIL,
      required: !!rules[fieldNames.CONTACT_INFO_USER_EMAIL].required,
      title: t('Email address'),
      placeholder: t('e.g. example@gmail.com'),
      rules: rules[fieldNames.CONTACT_INFO_USER_EMAIL],
      type: fieldTypes.INPUT,
      iconGlyph: 'mail',
      size: 'medium',
    },
    {
      id: fieldIds.contactInfoUserPhone,
      name: fieldNames.CONTACT_INFO_USER_PHONE,
      required: !!rules[fieldNames.CONTACT_INFO_USER_PHONE].required,
      title: t('Phone number'),
      placeholder: t('e.g. +1 234 567 8910'),
      rules: rules[fieldNames.CONTACT_INFO_USER_PHONE],
      type: fieldTypes.INPUT,
      iconGlyph: 'phone',
      size: 'medium',
    },
  ];
};

export default getFields;
